import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, INR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ReliabilityBadge } from "@/components/Badges";
import { Trash2, Pencil, UserCircle2, ReceiptText, Link2, Upload } from "lucide-react";
import { toast } from "sonner";
import BulkBar from "@/components/BulkBar";
import EntityFormDialog from "@/components/entities/EntityFormDialog";
import EntityFiltersBar from "@/components/entities/EntityFiltersBar";
import Pagination from "@/components/Pagination";
import ImportCSVDialog from "@/components/ImportCSVDialog";

const typeLabel = (t) => (t === "client" ? "Client" : "Partner");
const NONE = "__none__";
const emptyForm = {
  name: "",
  email: "",
  phone: "",
  gstin: "",
  address: "",
  notes: "",
  account_manager_id: NONE,
  payment_timeline_days: "30",
};

const ENTITY_IMPORT_PREVIEW_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "payment_timeline_days", label: "Terms (days)" },
];

const ENTITY_IMPORT_COLUMNS = [
  "name",
  "email",
  "phone",
  "gstin",
  "address",
  "payment_timeline_days",
  "notes",
];

const emptyFilters = {
  q: "",
  account_manager_id: "all",
  outstanding: "all", // all | has | paid | none
  reliability: "all", // all | on_time | slipping | late | none
};

export default function EntitiesPage({ type }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(emptyFilters);
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map((x) => x.id)));
  };
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} ${typeLabel(type).toLowerCase()}${ids.length === 1 ? "" : "s"}? Entities with invoices will be skipped.`)) return;
    try {
      const { data } = await api.post(`/entities/${type}/bulk-delete`, { ids });
      toast.success(`Deleted ${data.deleted}. Skipped ${data.skipped_with_invoices.length} with invoices.`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/entities/${type}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [type]);
  const loadManagers = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/directory`);
      setManagers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    loadManagers();
    setFilters(emptyFilters);
  }, [type, load, loadManagers]);

  const filteredItems = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return items.filter((it) => {
      if (q) {
        const hay = `${it.name || ""} ${it.email || ""} ${it.phone || ""} ${it.gstin || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.account_manager_id !== "all") {
        if (filters.account_manager_id === "none") {
          if (it.account_manager_id) return false;
        } else if (it.account_manager_id !== filters.account_manager_id) return false;
      }
      if (filters.outstanding === "has" && !(it.outstanding > 0)) return false;
      if (filters.outstanding === "paid" && (it.outstanding > 0 || it.total_invoices === 0)) return false;
      if (filters.outstanding === "none" && it.total_invoices > 0) return false;
      if (filters.reliability !== "all") {
        const s = it.score;
        if (filters.reliability === "none" && s !== null && s !== undefined) return false;
        if (filters.reliability === "on_time" && !(s !== null && s >= 85)) return false;
        if (filters.reliability === "slipping" && !(s !== null && s >= 60 && s < 85)) return false;
        if (filters.reliability === "late" && !(s !== null && s < 60)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const paginatedItems = useMemo(() => {
    if (pageSize === "All" || pageSize === 0) return filteredItems;
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize, type]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      email: it.email || "",
      phone: it.phone || "",
      gstin: it.gstin || "",
      address: it.address || "",
      notes: it.notes || "",
      account_manager_id: it.account_manager_id || NONE,
      payment_timeline_days: it.payment_timeline_days ? String(it.payment_timeline_days) : "30",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim() || !emailRe.test(form.email.trim())) return toast.error("Valid email is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    const timeline = parseInt(form.payment_timeline_days, 10);
    if (!timeline || timeline < 1 || timeline > 60) return toast.error("Payment timeline (1-60 days) is required");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        gstin: form.gstin || null,
        address: form.address || null,
        notes: form.notes || null,
        account_manager_id:
          form.account_manager_id && form.account_manager_id !== NONE
            ? form.account_manager_id
            : null,
        payment_timeline_days: timeline,
      };
      if (editing) await api.put(`/entities/${type}/${editing.id}`, payload);
      else await api.post(`/entities/${type}`, payload);
      toast.success(editing ? `${typeLabel(type)} updated` : `${typeLabel(type)} added`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try {
      await api.delete(`/entities/${type}/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetFilters = () => setFilters(emptyFilters);
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === "q") return v.trim() !== "";
    return v !== "all";
  }).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{typeLabel(type)} directory</div>
          <h1 className="font-display font-semibold text-xl sm:text-2xl tracking-tight mt-1">
            {type === "client" ? "Clients" : "Partners"}
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            Showing <span className="font-semibold text-ink tabular">{filteredItems.length}</span> of {items.length}
            {" "}{typeLabel(type).toLowerCase()}
            {items.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                className="rounded-sm"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" /> Import CSV
              </Button>
              <ImportCSVDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                title={`Import ${typeLabel(type)}s`}
                sampleUrl={`/entities/${type}/sample-csv`}
                sampleFilename={`sample_${type}s.csv`}
                importUrl={`/entities/${type}/import`}
                requiredColumns={ENTITY_IMPORT_COLUMNS}
                entityLabel={type}
                previewCountKey="would_create"
                previewColumns={ENTITY_IMPORT_PREVIEW_COLUMNS}
                onDone={() => { load(); }}
              />
            </>
          )}
          <EntityFormDialog
            open={open}
            onOpenChange={setOpen}
            editing={!!editing}
            form={form}
            setForm={setForm}
            managers={managers}
            typeLabel={typeLabel(type)}
            onOpenNew={openNew}
            onSave={save}
          />
        </div>
      </div>

      {/* Filters */}
      <EntityFiltersBar
        filters={filters}
        setFilters={setFilters}
        managers={managers}
        activeFilterCount={activeFilterCount}
        onReset={resetFilters}
      />

      <div className="border border-line bg-card overflow-x-auto">
        {filteredItems.length === 0 ? (
          <div className="p-12 text-center text-ink-2 text-sm" data-testid="entities-empty">
            {items.length === 0
              ? `No ${typeLabel(type).toLowerCase()}s yet. Add your first one.`
              : `No ${typeLabel(type).toLowerCase()}s match your filters.`}
          </div>
        ) : (
          <>
          <table className="w-full text-[13px] min-w-[720px]">
            <thead>
              <tr className="border-b border-line text-left">
                {isAdmin && (
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={selected.size > 0 && selected.size === filteredItems.length}
                      onChange={toggleAll}
                      data-testid="select-all"
                      className="cursor-pointer"
                      title={`Select all ${filteredItems.length} matching ${typeLabel(type).toLowerCase()}s (across pages)`}
                    />
                  </th>
                )}
                <th className="px-4 py-2.5 eyebrow">Name</th>
                <th className="px-4 py-2.5 eyebrow">Contact</th>
                <th className="px-4 py-2.5 eyebrow">Account Manager</th>
                <th className="px-4 py-2.5 eyebrow text-right">Terms</th>
                <th className="px-4 py-2.5 eyebrow text-right">Invoices</th>
                <th className="px-4 py-2.5 eyebrow text-right">Outstanding</th>
                <th className="px-4 py-2.5 eyebrow text-right">Reliability</th>
                <th className="px-4 py-2.5 eyebrow text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="entities-table">
              {paginatedItems.map((it) => (
                <tr key={it.id} className="row-divider hover:bg-[color:var(--bone)]">
                  {isAdmin && (
                    <td className="px-3 py-2.5 w-8">
                      <input
                        type="checkbox"
                        checked={selected.has(it.id)}
                        onChange={() => toggleOne(it.id)}
                        data-testid={`select-${it.id}`}
                        className="cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <div>{it.name}</div>
                    {it.gstin && (
                      <div className="text-[10px] uppercase tracking-wider text-ink-2">GSTIN {it.gstin}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-ink-2">
                    <div>{it.email || "-"}</div>
                    <div className="tabular">{it.phone || ""}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    {it.account_manager ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)] grid place-items-center text-[11px] font-semibold">
                          {it.account_manager.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="leading-tight">
                          <div className="text-ink">{it.account_manager.name}</div>
                          <div className="text-[10px] uppercase tracking-wider text-ink-2">
                            {it.account_manager.role.replace("_", " ")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-ink-2">
                        <UserCircle2 className="w-3.5 h-3.5" /> Unassigned
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular">
                    {it.payment_timeline_days ? (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 bg-[color:var(--bone)] border border-line rounded-sm text-[12px] font-semibold text-ink"
                        title={`Net ${it.payment_timeline_days} — invoices are due ${it.payment_timeline_days} days after invoice date`}
                        data-testid={`entity-terms-${it.id}`}
                      >
                        Net {it.payment_timeline_days}
                      </span>
                    ) : (
                      <span className="text-ink-2 text-[12px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular">{it.paid_invoices} / {it.total_invoices}</td>
                  <td className="px-4 py-2.5 text-right tabular">{INR(it.outstanding)}</td>
                  <td className="px-4 py-2.5 text-right"><ReliabilityBadge score={it.score} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Link
                      to={`/invoices?entity=${it.id}&type=${type}`}
                      title="View invoices"
                      data-testid={`view-invoices-${it.id}`}
                      className="inline-flex items-center h-7 px-2 rounded-sm hover:bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)]"
                    >
                      <ReceiptText className="w-3.5 h-3.5" />
                    </Link>
                    {it.portal_token && (
                      <button
                        type="button"
                        title="Copy portal link"
                        data-testid={`copy-portal-${it.id}`}
                        onClick={async () => {
                          const url = `${window.location.origin}/s/${it.portal_token}`;
                          try {
                            await navigator.clipboard.writeText(url);
                            toast.success("Portal link copied to clipboard");
                          } catch {
                            window.prompt("Copy this portal link:", url);
                          }
                        }}
                        className="inline-flex items-center h-7 px-2 rounded-sm hover:bg-[color:var(--aisensy-green-soft)] text-[color:var(--aisensy-dark)]"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-[color:var(--overdue)]" onClick={() => remove(it)} data-testid={`delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            total={filteredItems.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            entityLabel={`${typeLabel(type).toLowerCase()}s`}
          />
          </>
        )}
      </div>
      {isAdmin && (
        <BulkBar
          count={selected.size}
          onClear={() => setSelected(new Set())}
          onDelete={bulkDelete}
          entityLabel={typeLabel(type).toLowerCase()}
        />
      )}
    </div>
  );
}
